import { Field } from 'formik'
import isEqual from 'lodash-es/isEqual'
import React from 'react'
import { useParams } from 'react-router-dom'

import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { FeatureFlagDecorator } from '@components/utils/FeatureFlagDecorator'
import {
  Banner,
  ButtonClean,
  Checkbox,
  DayRange,
  Fieldset,
  Input,
  Select,
} from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { useAdminModeContext } from '@modules/App/providers/AdminModeProvider'
import { routing } from '@utils/routing'

import { TimezonedDatePicker } from '../../../../../../../Timezone/components/TimezonedDatePicker'
import { isShipmentStartDateEditable } from '../../../../../Product.utils'
import type { ProductFormState } from '../../../../SingleProduct.types'
import { useOfferPendingOrdersQuery } from '../../../../hooks/useOfferPendingOrdersQuery'
import * as S from '../../ProductShippingByCreator/styled'

const SHIPMENT_START_DATE_OPTIONS = [
  { value: 'UNKNOWN', label: 'No set shipping time' },
  { value: 'DAYS_RANGE', label: 'Number of days after the order is received' },
  { value: 'DATE_RANGE', label: 'On a specific date' },
]

const RangeSuggestion = () => (
  <>
    We use this to show your supporters when their order will ship.{' '}
    <ButtonClean
      label="Learn more"
      appearance="secondary"
      href={routing.external.helpCenter.selfSipping}
      target="_blank"
    />
  </>
)

export const ShippingStartDate = () => {
  const adminMode = useAdminModeContext()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { hasPermission } = useDashboardPermissions()
  const { productId = '' } = useParams<'productId'>()
  const { values, initialValues, errors, setFieldValue } = useFormikContext<ProductFormState>()

  const isFormEditable = hasPermission('products.edit.details')
  const disabled = !isFormEditable
  const isStartDateEditable = isShipmentStartDateEditable(values.manufacturingService)
  const { offerPendingOrdersQuery } = useOfferPendingOrdersQuery([{ offerId: productId }], {
    enabled: !!productId && !disabled,
  })
  const pendingOrdersCount = offerPendingOrdersQuery?.data?.count
  const hasShipmentChanged = !isEqual(initialValues.shipment, values.shipment)

  const handleDayRangeChange = async (value: [string?, string?]) => {
    await setFieldValue('shipment.days.daysMin', value[0])
    await setFieldValue('shipment.days.daysMax', value[1])
  }

  const handleDateRageChange = async ([startDate, endDate]: Date[]) => {
    await setFieldValue('shipment.dates.from', startDate)
    await setFieldValue('shipment.dates.to', endDate)
  }

  if (!isStartDateEditable) {
    return null
  }

  const hasDisabledShippingTimelines =
    !adminMode?.isActive &&
    (values.manufacturingService === 'FOURTHWALL_BESPOKE' ||
      (values.fulfillmentService === 'CART_JAZZ' &&
        values.manufacturingService !== 'MANUFACTURED_BY_CREATOR'))

  return (
    <>
      {dashboardFeatureFlags.productAdvancedOptions.enabled && (
        <Fieldset.Row>
          <Fieldset.Item>
            <div style={{ margin: '8px 0' }}>
              <FeatureFlagDecorator
                enabled
                description={dashboardFeatureFlags.productAdvancedOptions.description}
              >
                <Banner
                  title="Please confirm shipping times with production ops."
                  appearance="warning"
                />
              </FeatureFlagDecorator>
            </div>
          </Fieldset.Item>
        </Fieldset.Row>
      )}
      {hasDisabledShippingTimelines && (
        <Fieldset.Row>
          <Banner
            title={
              <>
                Shipping timelines can only be adjusted for creator manufactured products, please{' '}
                <a href="mailto:support@fourthwall.com" target="_blank" rel="noopener noreferrer">
                  reach out to support
                </a>{' '}
                for any questions.
              </>
            }
            appearance="warning"
          />
        </Fieldset.Row>
      )}
      <Fieldset.Row>
        <Fieldset.Item>
          <Field
            name="shipment.type"
            component={Select}
            options={SHIPMENT_START_DATE_OPTIONS}
            label="When will this product be shipped?"
            disabled={disabled || hasDisabledShippingTimelines}
          />
        </Fieldset.Item>
      </Fieldset.Row>
      <Fieldset.Row>
        <Fieldset.Item>
          {values.shipment.type === 'DAYS_RANGE' && (
            <DayRange
              name={['shipment.days.daysMin', 'shipment.days.daysMax']}
              value={[values.shipment.days.daysMin, values.shipment.days.daysMax]}
              suggestion={<RangeSuggestion />}
              error={errors?.shipment?.days?.daysMin || errors?.shipment?.days?.daysMax}
              width={232}
              disabled={disabled || hasDisabledShippingTimelines}
              onChange={handleDayRangeChange}
            />
          )}

          {values.shipment.type === 'DATE_RANGE' && (
            <TimezonedDatePicker
              type="range"
              name={['shipment.dates.from', 'shipment.dates.to']}
              value={[values.shipment.dates.from, values.shipment.dates.to]}
              suggestion={<RangeSuggestion />}
              error={errors?.shipment?.dates?.from || errors?.shipment?.dates?.to}
              disabled={disabled || hasDisabledShippingTimelines}
              onChange={handleDateRageChange}
            />
          )}
        </Fieldset.Item>
      </Fieldset.Row>
      {!!pendingOrdersCount && hasShipmentChanged && (
        <>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field
                name="shipmentDateUpdatePendingOrders"
                component={Checkbox}
                label={`Apply this change to all orders (${pendingOrdersCount}) that haven’t shipped yet`}
                disabled={disabled}
              />
            </Fieldset.Item>
          </Fieldset.Row>
          {values.shipment.type !== 'UNKNOWN' && values.shipmentDateUpdatePendingOrders && (
            <Fieldset.Row>
              <Fieldset.Item>
                <S.InnerField>
                  <Field
                    name="shipmentDateNotifyPendingSupporters"
                    component={Checkbox}
                    label="Notify supporters about the new estimated delivery date"
                    disabled={disabled}
                  />
                </S.InnerField>
              </Fieldset.Item>
            </Fieldset.Row>
          )}
          {values.shipment.type !== 'UNKNOWN' &&
            values.shipmentDateUpdatePendingOrders &&
            values.shipmentDateNotifyPendingSupporters && (
              <Fieldset.Row>
                <Fieldset.Item>
                  <S.InnerField>
                    <S.InnerField>
                      <Field
                        name="shipmentDateUpdateReason"
                        component={Input}
                        label="Reason (optional)"
                        suggestion="Reason will appear on a notification email sent to supporters."
                        disabled={disabled}
                      />
                    </S.InnerField>
                  </S.InnerField>
                </Fieldset.Item>
              </Fieldset.Row>
            )}
        </>
      )}
    </>
  )
}
